import { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, forwardRef } from 'react';

import reactToText from 'react-to-text';

import * as s from './TabLink.css';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../vanilla-extract.css';

type TabLinkRecipeVariants = RecipeVariants<typeof s.tabLinkRecipe>;

const sparkyClassName = 'sparky-tablink';

export const TabLink = forwardRef<HTMLLIElement, TabLinkProps>(
  ({ children, isCurrent = false, href, target, type = 'button', onClick, className = '' }, ref) => {
    const linkClass = getClassNames(sparkyClassName, s.tabLinkRecipe({ isCurrent }), className);

    const linkContent = href ? (
      <a
        href={href}
        target={target || undefined}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        aria-current={isCurrent ? 'page' : undefined}
        className={linkClass}
        data-label={reactToText(children)}
        onClick={onClick}>
        {children}
      </a>
    ) : (
      <button
        type={type}
        aria-current={isCurrent ? 'page' : undefined}
        className={linkClass}
        data-label={reactToText(children)}
        onClick={onClick}>
        {children}
      </button>
    );

    return (
      <li ref={ref} className={s.listItem}>
        {linkContent}
      </li>
    );
  },
);

TabLink.toString = () => `.${sparkyClassName}`;
TabLink.displayName = 'TabLink';

type TabLinkButtonProps = {
  href?: never;
  target?: never;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

type TabLinkAnchorProps = {
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  type?: never;
};

type TabLinkProps = TabLinkRecipeVariants & {
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /** Text shown inside the link. */
  children: ReactNode;
  className?: string;
} & (TabLinkAnchorProps | TabLinkButtonProps);
